type CantonNames = { fr: string; de: string; it: string; en: string; es: string };

export interface Canton {
  code: string;
  names: CantonNames;
}

// ES: no Swiss jurisdiction is Spanish-speaking. We follow Italian names as closest Romance language,
// with minor adjustments (Berna, Ginebra, Zúrich are established in Spanish).
const CANTONS: Canton[] = [
  { code: 'AG', names: { fr: 'Argovie', de: 'Aargau', it: 'Argovia', en: 'Aargau', es: 'Argovia' } },
  { code: 'AI', names: { fr: 'Appenzell Rhodes-Intérieures', de: 'Appenzell Innerrhoden', it: 'Appenzello Interno', en: 'Appenzell Innerrhoden', es: 'Appenzell Innerrhoden' } },
  { code: 'AR', names: { fr: 'Appenzell Rhodes-Extérieures', de: 'Appenzell Ausserrhoden', it: 'Appenzello Esterno', en: 'Appenzell Ausserrhoden', es: 'Appenzell Ausserrhoden' } },
  { code: 'BE', names: { fr: 'Berne', de: 'Bern', it: 'Berna', en: 'Bern', es: 'Berna' } },
  { code: 'BL', names: { fr: 'Bâle-Campagne', de: 'Basel-Landschaft', it: 'Basilea Campagna', en: 'Basel-Landschaft', es: 'Basilea Campiña' } },
  { code: 'BS', names: { fr: 'Bâle-Ville', de: 'Basel-Stadt', it: 'Basilea Città', en: 'Basel-Stadt', es: 'Basilea Ciudad' } },
  { code: 'FR', names: { fr: 'Fribourg', de: 'Freiburg', it: 'Friburgo', en: 'Fribourg', es: 'Friburgo' } },
  { code: 'GE', names: { fr: 'Genève', de: 'Genf', it: 'Ginevra', en: 'Geneva', es: 'Ginebra' } },
  { code: 'GL', names: { fr: 'Glaris', de: 'Glarus', it: 'Glarona', en: 'Glarus', es: 'Glarona' } },
  { code: 'GR', names: { fr: 'Grisons', de: 'Graubünden', it: 'Grigioni', en: 'Grisons', es: 'Grisones' } },
  { code: 'JU', names: { fr: 'Jura', de: 'Jura', it: 'Giura', en: 'Jura', es: 'Jura' } },
  { code: 'LU', names: { fr: 'Lucerne', de: 'Luzern', it: 'Lucerna', en: 'Lucerne', es: 'Lucerna' } },
  { code: 'NE', names: { fr: 'Neuchâtel', de: 'Neuenburg', it: 'Neuchâtel', en: 'Neuchâtel', es: 'Neuchâtel' } },
  { code: 'NW', names: { fr: 'Nidwald', de: 'Nidwalden', it: 'Nidvaldo', en: 'Nidwalden', es: 'Nidvaldo' } },
  { code: 'OW', names: { fr: 'Obwald', de: 'Obwalden', it: 'Obvaldo', en: 'Obwalden', es: 'Obvaldo' } },
  { code: 'SG', names: { fr: 'Saint-Gall', de: 'St. Gallen', it: 'San Gallo', en: 'St. Gallen', es: 'San Galo' } },
  { code: 'SH', names: { fr: 'Schaffhouse', de: 'Schaffhausen', it: 'Sciaffusa', en: 'Schaffhausen', es: 'Schaffhausen' } },
  { code: 'SO', names: { fr: 'Soleure', de: 'Solothurn', it: 'Soletta', en: 'Solothurn', es: 'Soleura' } },
  { code: 'SZ', names: { fr: 'Schwytz', de: 'Schwyz', it: 'Svitto', en: 'Schwyz', es: 'Schwyz' } },
  { code: 'TG', names: { fr: 'Thurgovie', de: 'Thurgau', it: 'Turgovia', en: 'Thurgau', es: 'Turgovia' } },
  { code: 'TI', names: { fr: 'Tessin', de: 'Tessin', it: 'Ticino', en: 'Ticino', es: 'Tesino' } },
  { code: 'UR', names: { fr: 'Uri', de: 'Uri', it: 'Uri', en: 'Uri', es: 'Uri' } },
  { code: 'VD', names: { fr: 'Vaud', de: 'Waadt', it: 'Vaud', en: 'Vaud', es: 'Vaud' } },
  { code: 'VS', names: { fr: 'Valais', de: 'Wallis', it: 'Vallese', en: 'Valais', es: 'Valais' } },
  { code: 'ZG', names: { fr: 'Zoug', de: 'Zug', it: 'Zugo', en: 'Zug', es: 'Zug' } },
  { code: 'ZH', names: { fr: 'Zurich', de: 'Zürich', it: 'Zurigo', en: 'Zurich', es: 'Zúrich' } },
];

export type CantonOption = { value: string; label: string };

/**
 * Returns canton options sorted by localized name, with code prefix: "GE — Genève".
 * @param langCode - one of 'fr', 'de', 'it', 'en', 'es' (case-insensitive)
 */
export function getCantonOptions(langCode: string): CantonOption[] {
  const key = langCode.toLowerCase() as keyof CantonNames;
  return CANTONS
    .map(c => ({
      value: c.code,
      label: `${c.code} — ${c.names[key] || c.names.en}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, key));
}

/**
 * Returns the localized name of a canton given its code.
 */
export function getCantonName(code: string, langCode: string): string {
  const key = langCode.toLowerCase() as keyof CantonNames;
  const canton = CANTONS.find(c => c.code === code);
  return canton ? canton.names[key] || canton.names.en : code;
}

export const CANTON_CODES = CANTONS.map(c => c.code);

/** Removes diacritics for accent-insensitive matching. */
function normalize(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

// Pre-built lookup: normalized name → Canton (all languages + codes)
const RESOLVE_MAP = new Map<string, Canton>();
for (const c of CANTONS) {
  RESOLVE_MAP.set(normalize(c.code), c);
  for (const lang of ['fr', 'de', 'it', 'en', 'es'] as const) {
    RESOLVE_MAP.set(normalize(c.names[lang]), c);
  }
}

/**
 * Resolves an arbitrary string to a known Canton.
 *
 * Accepts a code ("GE", "ge") or a name in any of the five languages
 * ("Genève", "Genf", "Ginevra", "Geneva", "Ginebra").
 * Case-insensitive, accent-insensitive.
 *
 * Returns the Canton object if found, or null.
 */
export function resolveCanton(value: string): Canton | null {
  if (!value) return null;
  return RESOLVE_MAP.get(normalize(value)) ?? null;
}

/** Affichage: nom complet localisé si canton suisse reconnu (code ou nom), sinon la valeur brute. */
export function formatCantonDisplay(value: string | null | undefined, langCode: string): string {
  if (!value) return '';
  const c = resolveCanton(value);
  return c ? getCantonName(c.code, langCode) : value;
}
